import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  // FileText,
  // Download,
} from "lucide-react";

const DataTable = ({
  columns,
  data,
  loading = false,
  pagination = true,
  initialRowsPerPage = 10,
  rowsPerPageOptions = [5, 10, 25, 50],
  searchable = true,
  searchFields = [],
  actions = true,
  customActions = null,
  onRowClick = null,
  actionColumn = true,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    let result = [...data];

    // Apply search filter
    if (searchTerm && searchFields.length > 0) {
      const searchTermLower = searchTerm.toLowerCase();
      result = result.filter((row) =>
        searchFields.some(
          (field) =>
            row[field] &&
            String(row[field]).toLowerCase().includes(searchTermLower)
        )
      );
    }

    // Apply sorting
    if (sortBy) {
      result.sort((a, b) => {
        const aValue = a[sortBy] || "";
        const bValue = b[sortBy] || "";

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortDirection === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }
      });
    }

    setFilteredData(result);
    setPage(0); // Reset to first page when filtering
  }, [data, searchTerm, sortBy, sortDirection, searchFields]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Default action components
  const defaultActions = (row) => (
    <div className="flex space-x-2">
      <IconButton
        className="text-blue-600 hover:text-blue-800"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // View action
          if (typeof customActions?.onView === "function") {
            customActions.onView(row);
          }
        }}
      >
        <Eye className="w-5 h-5" />
      </IconButton>
      {/* <IconButton
        className="text-red-600 hover:text-red-800"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // Download action
          if (typeof customActions?.onDownload === "function") {
            customActions.onDownload(row);
          }
        }}
      >
        <Download className="w-5 h-5" />
      </IconButton>
      <IconButton
        className="text-gray-600 hover:text-gray-800"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          // Details action
          if (typeof customActions?.onDetails === "function") {
            customActions.onDetails(row);
          }
        }}
      >
        <FileText className="w-5 h-5" />
      </IconButton> */}
    </div>
  );

  // Calculate paginated data
  const paginatedData = pagination
    ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredData;

  return (
    <div className="w-full">
      {searchable && (
        <div className="mb-4">
          <TextField
            variant="outlined"
            placeholder="Search..."
            fullWidth
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="w-5 h-5 text-gray-500" />
                </InputAdornment>
              ),
            }}
            className="bg-white rounded-lg"
          />
        </div>
      )}

      <Paper className="overflow-hidden w-full rounded-xl shadow-md">
        <TableContainer className="max-h-[600px]">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <CircularProgress color="primary" />
            </div>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || "left"}
                      style={{
                        minWidth: column.minWidth,
                        fontWeight: "bold",
                        backgroundColor: "#f9fafb",
                      }}
                      sortDirection={
                        sortBy === column.id ? sortDirection : false
                      }
                    >
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={
                            sortBy === column.id ? sortDirection : "asc"
                          }
                          onClick={() => handleRequestSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  {actionColumn && actions && (
                    <TableCell
                      align="right"
                      style={{
                        minWidth: 120,
                        fontWeight: "bold",
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <TableRow
                      hover
                      key={row.id || index}
                      onClick={() => onRowClick && onRowClick(row)}
                      style={{ cursor: onRowClick ? "pointer" : "default" }}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align || "left"}
                          >
                            {column.render ? column.render(row) : value}
                          </TableCell>
                        );
                      })}
                      {actionColumn && actions && (
                        <TableCell align="right">
                          {customActions?.render
                            ? customActions.render(row)
                            : defaultActions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={
                        columns.length + (actionColumn && actions ? 1 : 0)
                      }
                      align="center"
                      className="py-8"
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* {pagination && (
          <div className="flex justify-between items-center px-4 py-2 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {Math.min(filteredData.length, page * rowsPerPage + 1)} to{" "}
              {Math.min(filteredData.length, page * rowsPerPage + rowsPerPage)}{" "}
              of {filteredData.length} entries
            </div>
            <div className="flex items-center">
              <IconButton
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 0}
                className={page === 0 ? "text-gray-300" : "text-gray-700"}
              >
                <ChevronLeft className="w-5 h-5" />
              </IconButton>
              <div className="px-4 text-sm">
                Page {page + 1} of{" "}
                {Math.max(1, Math.ceil(filteredData.length / rowsPerPage))}
              </div>
              <IconButton
                onClick={() => handleChangePage(null, page + 1)}
                disabled={
                  page >= Math.ceil(filteredData.length / rowsPerPage) - 1
                }
                className={
                  page >= Math.ceil(filteredData.length / rowsPerPage) - 1
                    ? "text-gray-300"
                    : "text-gray-700"
                }
              >
                <ChevronRight className="w-5 h-5" />
              </IconButton>
            </div>
            <div>
              <select
                className="px-3 py-2 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
              >
                {rowsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option} per page
                  </option>
                ))}
              </select>
            </div>
          </div>
        )} */}
      </Paper>
    </div>
  );
};

export default DataTable;
